import { z } from "zod";
import { PatientAddressSchema, PatientAdministrativeSexSchema, PatientCoverageUpdateInputSchema, PatientEmergencyContactSchema, PatientProfileSchema, } from "./patient-profile.js";
import { IdempotencyKeySchema, IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Patient registration without a visit (owner decision 2026-09-03): the front
 * desk registers a patient today and the clinician does intake at the first
 * visit. Mirrors the walk-in intake minus the visit fields (chief complaint,
 * urgency) plus the optional address and emergency contact the demographics
 * writer already accepts. Migration 20260904120044:
 * `api.register_patient` (idempotent on the key, receipt carries the
 * role-projected profile) and `api.find_possible_duplicate_patients` (a soft
 * probe on name + date of birth or the provincial/federal health number; the
 * decision stays with the person registering, nothing is blocked).
 */
const IsoDateSchema = z.string().date();
export const RegisterPatientInputSchema = z
    .object({
    legalFirstName: z.string().trim().min(1).max(100),
    /** Optional legal middle name(s), migration 20261001202120. */
    legalMiddleName: z.string().trim().min(1).max(100).optional(),
    legalLastName: z.string().trim().min(1).max(100),
    preferredName: z.string().trim().min(1).max(120).optional(),
    dateOfBirth: IsoDateSchema,
    administrativeSex: PatientAdministrativeSexSchema,
    phone: z.string().trim().min(1).max(40).optional(),
    email: z.string().trim().min(3).max(320).optional(),
    address: PatientAddressSchema.optional(),
    emergencyContact: PatientEmergencyContactSchema.optional(),
    /**
     * The coverage plus the health card version code and expiry date a
     * provincial plan carries, validated as the patient profile validates them
     * (EXECDEV-111 / EXECDEV-210, migration 20261007022422). An absent or empty
     * one records none.
     */
    coverage: PatientCoverageUpdateInputSchema,
})
    .strict();
export const RegisterPatientRequestSchema = z
    .object({
    siteId: IdSchema,
    idempotencyKey: IdempotencyKeySchema,
    input: RegisterPatientInputSchema,
})
    .strict();
/** `created: false` is an idempotent replay; the profile is projected for the caller's role. */
export const RegisterPatientReceiptSchema = z
    .object({
    created: z.boolean(),
    patientId: IdSchema,
    mrn: z.string().min(1),
    profile: PatientProfileSchema,
})
    .passthrough();
/**
 * A probe needs one complete criterion: first + last name + date of birth, or
 * a health number (the provincial/federal coverage member id).
 */
export const DuplicatePatientProbeSchema = z
    .object({
    legalFirstName: z.string().trim().min(1).max(100).optional(),
    legalLastName: z.string().trim().min(1).max(100).optional(),
    dateOfBirth: IsoDateSchema.optional(),
    healthNumber: z.string().trim().min(2).max(120).optional(),
})
    .strict()
    .superRefine((probe, context) => {
    const nameAndDob = probe.legalFirstName !== undefined &&
        probe.legalLastName !== undefined &&
        probe.dateOfBirth !== undefined;
    if (!nameAndDob && probe.healthNumber === undefined) {
        context.addIssue({
            code: "custom",
            message: "A duplicate probe needs first name, last name and date of birth, or a health number",
        });
    }
});
export const DuplicatePatientProbeRequestSchema = z
    .object({ siteId: IdSchema, probe: DuplicatePatientProbeSchema })
    .strict();
export const DuplicatePatientMatchReasonSchema = z.enum(["name_and_dob", "health_number"]);
export const DuplicatePatientMatchSchema = z
    .object({
    patientId: IdSchema,
    mrn: z.string().min(1),
    displayName: z.string().min(1),
    dateOfBirth: IsoDateSchema,
    administrativeSex: PatientAdministrativeSexSchema.optional(),
    matchedOn: z.array(DuplicatePatientMatchReasonSchema).min(1),
    registeredAt: IsoDateTimeSchema.optional(),
    lastVisitAt: IsoDateTimeSchema.optional(),
})
    .passthrough();
export const DuplicatePatientMatchesSchema = z
    .object({ matches: z.array(DuplicatePatientMatchSchema).max(10) })
    .passthrough();
//# sourceMappingURL=patient-registration.js.map